import { useState, useCallback, useEffect } from "react";
import type { SortingState, OnChangeFn } from "@tanstack/react-table";

import { readSortFromURL, writeSortToURL } from "../utils";

export interface SortInfo {
  field: string;
  order: "ascend" | "descend";
}

interface UseTableSortOptions {
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  onSort?: (sort: SortInfo | null) => void;
  enableURLSort?: boolean;
}

export const useTableSort = ({
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
  onSort,
  enableURLSort = true,
}: UseTableSortOptions) => {
  const isControlled = controlledSorting !== undefined;

  const [internalSorting, setInternalSorting] = useState<SortingState>(() =>
    enableURLSort ? readSortFromURL() : []
  );

  const sorting = isControlled ? controlledSorting : internalSorting;

  const onSortingChange: OnChangeFn<SortingState> = useCallback(
    updaterOrValue => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(sorting)
          : updaterOrValue;

      if (!isControlled) {
        setInternalSorting(newSorting);
      }

      controlledOnSortingChange?.(updaterOrValue);

      if (onSort) {
        onSort(
          newSorting.length > 0
            ? {
                field: newSorting[0].id,
                order: newSorting[0].desc ? "descend" : "ascend",
              }
            : null
        );
      }

      if (enableURLSort) {
        writeSortToURL(newSorting);
      }
    },
    [sorting, isControlled, controlledOnSortingChange, onSort, enableURLSort]
  );

  useEffect(() => {
    if (enableURLSort && !isControlled) {
      const urlSorting = readSortFromURL();
      setInternalSorting(urlSorting);
    }
  }, [enableURLSort, isControlled]);

  return { sorting, onSortingChange };
};
